// ✅ Interfaces (strong typing)

export interface PromptField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  required: boolean;
  desc?: string;
  options?: string[];
  default?: string;
  placeholder?: string;
}

export interface Prompt {
  //group: 'plan' | 'draft' | 'prepare' | 'analyze';
  group: 'Plan' | 'Draft' | 'Prepare' | 'Analyze' | 'Compliance';
  name: string;
  description: string;
  bring: string;
  template: string;
  fields: PromptField[];
}

export const PROMPTS: readonly Prompt[] = [
  {
    group: "Plan",
    name: "Stakeholder Engagement Strategy",
    description: "Build a targeted engagement strategy for a specific stakeholder group — covering relationship context, tailored messaging, engagement tactics, objection handling, and a path forward if the relationship turns adversarial.",
    bring: "Bring background on the stakeholder group, any prior engagement history, and known positions or concerns.",
    template: `Act as a [ROLE] for a major Fortune 50 energy company. Your objective is to build a genuine engagement strategy — not a one-time outreach plan — that moves a stakeholder relationship toward trust, alignment, or constructive working partnership.
      
Develop a targeted stakeholder engagement strategy for the following situation.
      
Context:
Stakeholder Group: [STAKEHOLDER_GROUP]
Engagement Focus: [ENGAGEMENT_FOCUS]
Current Relationship Status: [RELATIONSHIP_STATUS]
Engagement Objective: [ENGAGEMENT_OBJECTIVE]
Timeline: [TIMELINE]
      
Approach:
• Use a [TONE] approach throughout all engagements
• Understand the stakeholder before designing tactics — their interests, power, influence, prior positions, and history with this company are the foundation; tactics without this context tend to backfire
• Design for two-way dialogue, not message delivery; what the company learns from this engagement matters as much as what it says
• Account for any unmet past commitments — they are the fastest way to lose credibility before the first conversation even starts
      
Deliver a strategy that covers:
• A stakeholder profile — who they are, what they care about, and their history with Chevron
• Specific, measurable engagement objectives
• Key messages tailored to what this stakeholder actually cares about, not what the company wants to say
• Recommended engagement activities, channels, and a realistic timeline with milestones
• Anticipated objections and how to handle them
• A path forward if the engagement stalls or turns adversarial
      
Use your judgment to build the right level of structure for the complexity of this relationship.
      
Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "ROLE",
        label: "Role",
        type: "select",
        required: true,
        desc: "Default: Stakeholder Engagement Lead",
        options: [
          "Stakeholder Engagement Lead",
          "Public Affairs Advisor",
          "Community Investment Advisor",
          "Government Policy Advisor",
          "Sr. Communications Advisor"
        ],
        default: "Stakeholder Engagement Lead"
      },
      {
        id: "STAKEHOLDER_GROUP",
        label: "Stakeholder Group",
        type: "select",
        required: true,
        desc: "Who is the target stakeholder group?",
        options: [
          "Government Officials",
          "Regulators",
          "Community Stakeholders",
          "NGOs / Advocacy Groups",
          "Industry Partners",
          "Tribal Nations / Indigenous Communities",
          "Academic / Research Institutions",
          "Labor Organizations"
        ]
      },
      {
        id: "ENGAGEMENT_FOCUS",
        label: "Engagement Focus",
        type: "text",
        required: true,
        desc: "What is the primary topic or issue driving this engagement?",
        placeholder: "e.g., Pipeline expansion through tribal lands — initial nation-to-nation consultation required before federal permitting process begins..."
      },
      {
        id: "RELATIONSHIP_STATUS",
        label: "Relationship Status",
        type: "select",
        required: true,
        desc: "What is the current state of this relationship?",
        options: [
          "New relationship — no prior history",
          "Existing, positive relationship",
          "Neutral / transactional",
          "Strained — prior conflict or unresolved commitments",
          "Recovering — actively rebuilding after a difficult period"
        ]
      },
      {
        id: "ENGAGEMENT_OBJECTIVE",
        label: "Engagement Objective",
        type: "text",
        required: true,
        desc: "What specific outcome are you working toward?",
        placeholder: "e.g., Secure community support before the public comment period; address water quality monitoring concerns with binding commitments..."
      },
      {
        id: "TIMELINE",
        label: "Timeline",
        type: "text",
        required: false,
        desc: "Timeframe for this engagement (optional)",
        placeholder: "e.g., 90-day engagement plan; initial meeting targeted before end of month..."
      },
      {
        id: "TONE",
        label: "Tone",
        type: "select",
        required: true,
        desc: "Tone and approach for all engagement activities",
        options: [
          "Formal",
          "Confident",
          "Casual",
          "Chevron Brand"
        ]
      }
    ]
  },
  {
    group: "Prepare",
    name: "Weekly Report Bullets (GPA)",
    description: "Generate concise, executive-ready bullets from your calendar, emails, chats, and notes for inclusion in a leadership weekly report. Filters for needle movers only and writes in the voice your leaders expect.",
    bring: "No upload required. The prompt reviews your calendar, emails, chats, and notes for the selected time period. Optionally paste in any extra context (drafts, prior bullets, anchor activities) that may not be reflected in your Microsoft 365 data.",
    template: `Act as a [ROLE].

Review my calendar, emails, chats, and notes from [TIME_PERIOD] and generate [BULLET_COUNT] concise weekly report bullets suitable for inclusion in the [REPORT_NAME].

Requirements:
- Focus only on "needle movers" — activities with clear business, regulatory, stakeholder, reputational, or commercial impact.
- Exclude routine or informational activities unless they directly advanced a priority outcome.
- Each bullet should be 1 to 2 sentences maximum.
- Begin each bullet with a strong action verb (e.g., Advanced, Supported, Secured, Led, Coordinated).
- Clearly state:
  • what happened
  • why it matters (business value, risk reduction, stakeholder impact, or project advancement)
- Include outcomes or directional results where available (e.g., sentiment, approvals, milestones, expected timing).

Style and tone:
- Executive-ready, concise, and polished.
- Written in the voice and expectations of [LEADERSHIP_VOICE].
- No filler. No unnecessary internal jargon. No acronyms unless unavoidable.
- Prioritize clarity, impact, and strategic relevance.

Final pass:
- Tighten language by approximately 20%.
- Remove redundancy.
- Ensure bullets are ready to paste directly into a leadership-facing weekly report.

Additional context (optional):
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "ROLE",
        label: "Role",
        type: "select",
        required: true,
        desc: "Default: Corporate Affairs advisor",
        options: [
          "Corporate Affairs advisor",
          "Government and Public Affairs advisor",
          "Strategy and Operations advisor",
          "Sr. Communications advisor"
        ],
        default: "Corporate Affairs advisor"
      },
      {
        id: "TIME_PERIOD",
        label: "Time Period",
        type: "select",
        required: true,
        desc: "Time window to scan",
        options: [
          "the past week",
          "the past two weeks",
          "this month",
          "the current sprint"
        ],
        default: "the past week"
      },
      {
        id: "BULLET_COUNT",
        label: "Bullet Count",
        type: "select",
        required: true,
        desc: "How many bullets to produce",
        options: [
          "3 to 5",
          "3",
          "5",
          "5 to 7",
          "7"
        ],
        default: "3 to 5"
      },
      {
        id: "REPORT_NAME",
        label: "Report Name",
        type: "text",
        required: true,
        desc: "The report the bullets will be pasted into",
        placeholder: "Corporate Affairs GPA weekly report",
        default: "Corporate Affairs GPA weekly report"
      },
      {
        id: "LEADERSHIP_VOICE",
        label: "Leadership Voice",
        type: "text",
        required: true,
        desc: "Whose voice and expectations should drive the bullets",
        placeholder: "Nicole Barber and Margaret Cooper",
        default: "Nicole Barber and Margaret Cooper"
      }
    ]
  },
  {
    group: "Prepare",
    name: "Executive Communications Brief",
    description: "Convert complex source material into an executive-ready briefing — summary paragraph first, top 2–3 things to know, key risk or unknown, and a clear recommended action.",
    bring: "Bring the source material you need the executive to act on — a report, article, memo, regulatory filing, or briefing document.",
    template: `Act as a [ROLE] for a major Fortune 50 energy company. Your objective is to equip a senior executive with exactly what they need to make a decision, walk into a meeting fully prepared, or align with their peers — written on the assumption that they will read the first paragraph and may not read anything else.
      
Convert the following material into an executive-ready briefing for [AUDIENCE].
      
Context:
Topic: [TOPIC_ISSUE]
Use Case: [USE_CASE]
Urgency: [URGENCY]
Sensitivity: [SENSITIVITY]
      
Approach:
• Use a [TONE] tone — clear, direct, and calibrated to the executive's role and familiarity with this topic
• Write the summary as if it is the only thing this person will read; the most critical point, any required action, and the key risk all belong in the first paragraph
• Flag uncertainty and risk explicitly — an executive who is surprised in a meeting by something that was buried in the brief has not been well served
      
Deliver:
• A summary paragraph (3–4 sentences) — the key point first, written for someone who may read nothing else
• The 2–3 things this executive most needs to know, in plain language
• One "what to watch" — the risk, unknown, or developing factor that could change the picture
• A clear recommended action — decision needed, call to make, or no action required at this time
      
Everything else serves the summary. If it does not change what the executive does or thinks, it does not belong in the brief.
      
Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "ROLE",
        label: "Role",
        type: "select",
        required: true,
        desc: "Default: Sr. Communications Advisor",
        options: [
          "Sr. Communications Advisor",
          "Executive Communications Lead",
          "Issues Management Advisor",
          "Corporate Communications Manager",
          "Public Affairs Advisor"
        ],
        default: "Sr. Communications Advisor"
      },
      {
        id: "AUDIENCE",
        label: "Audience",
        type: "select",
        required: true,
        desc: "Who will receive this briefing?",
        options: [
          "ELT (Executive Leadership Team)",
          "CEO / Chairman",
          "Board of Directors",
          "Executive Vice President",
          "Senior Vice President"
        ]
      },
      {
        id: "TOPIC_ISSUE",
        label: "Topic / Issue",
        type: "text",
        required: true,
        desc: "Be specific: include stakeholders, timeline, and context",
        placeholder: "e.g., Proposed federal methane rule impacting Permian Basin operations — EPA comment period closes June 30, estimated $140M compliance cost..."
      },
      {
        id: "USE_CASE",
        label: "Use Case",
        type: "select",
        required: true,
        desc: "What decision or meeting is this briefing supporting?",
        options: [
          "Leadership review",
          "Board preparation",
          "Decision support",
          "Stakeholder alignment",
          "Pre-meeting preparation",
          "Regulatory response preparation"
        ]
      },
      {
        id: "URGENCY",
        label: "Urgency",
        type: "select",
        required: true,
        desc: "How time-sensitive is this briefing?",
        options: [
          "Routine — standard review cycle",
          "Time-sensitive — needed within 48–72 hours",
          "Urgent — needed same day"
        ]
      },
      {
        id: "SENSITIVITY",
        label: "Sensitivity",
        type: "select",
        required: true,
        desc: "Classification level for handling and distribution",
        options: [
          "Standard",
          "Confidential",
          "Highly Sensitive — Limited Distribution"
        ]
      },
      {
        id: "TONE",
        label: "Tone",
        type: "select",
        required: true,
        desc: "Communication style for this briefing",
        options: [
          "Formal",
          "Confident",
          "Chevron Brand"
        ]
      }
    ]
  },
  {
    group: "Prepare",
    name: "Speaking Notes / Talking Points",
    description: "Prepare a speaker to sound like a confident, credible leader — with an opening hook, a core narrative arc, anchored talking points, anticipated Q&A including the hardest questions, and a memorable close.",
    bring: "Bring background on the speaker, event context, prior remarks or known positions, and any recent news the audience will have in mind.",
    template: `Act as a [ROLE] for a major Fortune 50 energy company. Your objective is to prepare a speaker to sound like a confident, credible leader with a clear point of view — not a spokesperson reading from prepared remarks.
      
Prepare speaking notes and talking points for the following engagement.
      
Context:
Speaker: [SPEAKER]
Audience: [AUDIENCE]
Event / Setting: [EVENT_SETTING]
Format: [FORMAT]
Duration: [DURATION]
Priority Message: [PRIORITY_MESSAGE]
      
Approach:
• Use a [TONE] tone — write in natural, spoken language; test every sentence by saying it aloud
• Every point should be memorable, specific, and grounded in a real example, data point, or story; vague leadership language lands as nothing
• Prepare the speaker for what this audience is most anxious or skeptical about, not just what they are expected to be asked
• Build in moments where the speaker acknowledges complexity or uncertainty — audiences trust speakers who don't pretend to have all the answers
      
Deliver:
• An opening hook — the first 30 seconds that earns the room's attention; no "thank you for having me"
• A core narrative arc that connects the key points and builds toward the priority message
• 3–5 talking points, each anchored in a specific example or data point
• Anticipated Q&A with full responses, including the hardest and most uncomfortable questions
• A closing that gives the audience one thing to remember and, where appropriate, a clear call to action
      
Write in the speaker's voice. If their style is known, the material should sound like them — not like it was written for someone else.
      
Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "ROLE",
        label: "Role",
        type: "select",
        required: true,
        desc: "Default: Executive Communications Lead",
        options: [
          "Executive Communications Lead",
          "Sr. Communications Advisor",
          "Government Policy Advisor",
          "Media Relations Specialist",
          "Public Affairs Advisor"
        ],
        default: "Executive Communications Lead"
      },
      {
        id: "SPEAKER",
        label: "Speaker",
        type: "text",
        required: false,
        desc: "Name and title of the person speaking (optional)",
        placeholder: "e.g., Mike Wirth, Chairman and CEO, Chevron Corporation..."
      },
      {
        id: "AUDIENCE",
        label: "Audience",
        type: "select",
        required: true,
        desc: "Who will be in the room or watching?",
        options: [
          "Investors / Analysts",
          "Media",
          "Government Officials / Legislators",
          "Employees",
          "Community Stakeholders",
          "Industry Peers / Conference Attendees",
          "Board of Directors",
          "General Public"
        ]
      },
      {
        id: "EVENT_SETTING",
        label: "Event / Setting",
        type: "text",
        required: true,
        desc: "What is the event, the host organization, and its context?",
        placeholder: "e.g., CERAWeek by S&P Global — panel on energy transition investment, Houston, February 2025, ~2,000 attendees..."
      },
      {
        id: "FORMAT",
        label: "Format",
        type: "select",
        required: true,
        desc: "What is the speaking format?",
        options: [
          "Keynote address — solo presentation",
          "Panel discussion — moderated with other speakers",
          "Media interview — one-on-one",
          "Town hall — employee Q&A",
          "Congressional or regulatory testimony",
          "Investor day / earnings call",
          "Community meeting or public forum"
        ]
      },
      {
        id: "DURATION",
        label: "Duration",
        type: "select",
        required: true,
        desc: "How long is the speaking slot?",
        options: [
          "2–3 minutes — brief remarks",
          "5–10 minutes — short presentation",
          "15–20 minutes — standard keynote",
          "30–45 minutes — extended with Q&A",
          "60+ minutes — full session"
        ]
      },
      {
        id: "PRIORITY_MESSAGE",
        label: "Priority Message",
        type: "text",
        required: true,
        desc: "The #1 thing this audience must remember after this talk",
        placeholder: "e.g., Chevron is investing more in lower-carbon energy than at any point in its history, while reliably meeting today's energy needs..."
      },
      {
        id: "TONE",
        label: "Tone",
        type: "select",
        required: true,
        desc: "Speaking tone and delivery style",
        options: [
          "Formal",
          "Confident",
          "Casual",
          "Chevron Brand"
        ]
      }
    ]
  },
  {
    group: "Prepare",
    name: "Q&A Preparation: Internal",
    description: "Prepare a leader for an employee session with realistic questions — including the uncomfortable and emotional ones — with bridge phrases, suggested answers, and a protocol for when the room goes quiet.",
    bring: "Bring the announcement or change details, any early employee feedback or pulse data, and known concerns from managers or HR.",
    template: `Act as a [ROLE] for a major Fortune 50 energy company. Your objective is to prepare a leader to walk into an employee session fully ready — including for the questions that will test their credibility, their composure, and their commitment to transparency.
      
Prepare an internal Q&A document for the following engagement.
      
Context:
Internal Audience: [INTERNAL_AUDIENCE]
Topic / Announcement: [TOPIC]
Engagement Format: [FORMAT]
Key Message to Reinforce: [KEY_MESSAGE]
Known Employee Concerns: [KNOWN_CONCERNS]
      
Approach:
• Use a [TONE] tone — write questions exactly as an employee would actually ask them, including the uncomfortable, emotional, and skeptical ones; sanitized Q&A does not prepare anyone for a real room
• Answers must be honest, direct, and human; corporate-speak and empty reassurances are spotted immediately and they make things worse
• Prepare the leader for the emotional dimensions of the session — not just the questions, but what to do when an employee becomes visibly upset or the room goes quiet
      
Deliver a Q&A document that covers:
• An opening statement that sets the tone, builds trust, and signals the leader is there to listen as well as speak
• The range of questions employees are likely to raise — from procedural to substantive to hard and emotional
• For the hardest questions: a bridge phrase that acknowledges the concern genuinely, a full suggested answer, and a note on what to avoid saying and why
• A protocol for moments of silence, emotion, or public confrontation
• A closing that reinforces the key message and confirms what happens next
      
Calibrate the depth and number of questions to the complexity of the topic and the size of the session.
      
Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "ROLE",
        label: "Role",
        type: "select",
        required: true,
        desc: "Default: Sr. Communications Advisor",
        options: [
          "Sr. Communications Advisor",
          "Executive Communications Lead",
          "Corporate Communications Manager",
          "Issues Management Advisor"
        ],
        default: "Sr. Communications Advisor"
      },
      {
        id: "INTERNAL_AUDIENCE",
        label: "Internal Audience",
        type: "select",
        required: true,
        desc: "Who will be in the session?",
        options: [
          "All employees (company-wide town hall)",
          "Business unit or division",
          "People managers only",
          "Senior leadership team",
          "Specific site or location",
          "Mixed — employees and managers together"
        ]
      },
      {
        id: "TOPIC",
        label: "Topic / Announcement",
        type: "text",
        required: true,
        desc: "What is this session about?",
        placeholder: "e.g., Organizational restructuring merging two divisions, effective Q3, affecting ~1,200 employees across 4 locations..."
      },
      {
        id: "FORMAT",
        label: "Engagement Format",
        type: "select",
        required: true,
        desc: "What type of session is this?",
        options: [
          "All-hands / Town hall (large group, in-person or virtual)",
          "Manager cascade session",
          "Skip-level listening session",
          "Small group dialogue",
          "1-on-1 or team check-in"
        ]
      },
      {
        id: "KEY_MESSAGE",
        label: "Key Message to Reinforce",
        type: "text",
        required: true,
        desc: "The one thing employees must walk away believing",
        placeholder: "e.g., This change strengthens our competitive position and every employee's contributions are valued in the new structure..."
      },
      {
        id: "KNOWN_CONCERNS",
        label: "Known Employee Concerns",
        type: "text",
        required: false,
        desc: "What are employees already worried about? (optional)",
        placeholder: "e.g., Job security, new reporting lines, relocation requirements, timeline uncertainty, who made this decision..."
      },
      {
        id: "TONE",
        label: "Tone",
        type: "select",
        required: true,
        desc: "Overall tone for this Q&A session",
        options: [
          "Formal",
          "Confident",
          "Casual",
          "Chevron Brand"
        ]
      }
    ]
  },
  {
    group: "Prepare",
    name: "Q&A Preparation: External / Media",
    description: "Prepare a spokesperson for an external engagement with tough, well-researched questions — from expected to adversarial — with bridge language, full suggested responses, and protocol for sensitive topics.",
    bring: "Bring recent coverage on this topic, any prior spokesperson statements, and known vulnerabilities in the company's current position.",
    template: `Act as a [ROLE] for a major Fortune 50 energy company. Your objective is to prepare a spokesperson to walk into an external engagement ready for every question — including the ones a hostile journalist, an activist investor, or an adversarial regulator has been researching for weeks.
      
Prepare an external Q&A document for the following engagement.
      
Context:
Audience Type: [EXTERNAL_AUDIENCE]
Topic: [TOPIC]
Engagement Setting: [SETTING]
Key Message to Protect: [KEY_MESSAGE]
Topics We Cannot Comment On: [OFF_LIMITS]
      
Approach:
• Use a [TONE] tone — write questions as a well-researched skeptic would actually ask them; if the hard version of a question isn't in this document, the spokesperson will face it unprepared
• Answers must be complete, strategically sound, and defensible — evasive or lawyerly-sounding responses invite more pressure, not less
• Brief the spokesperson on what this audience has been reading and watching recently; they are not coming in with a blank slate
      
Deliver a Q&A document that covers:
• A one-paragraph spokesperson briefing: who this audience is, what they care about, and what news context they are bringing into the room
• The range of questions from expected and low-risk to substantive and adversarial
• For the hardest questions: bridge language for pivoting, a full suggested response, and a clear note on what not to say
• A protocol for sensitive topics — what can be discussed on the record, what should go background, and how to redirect what cannot be addressed
      
The hardest questions in this document should reflect genuine research and edge — not diplomatic softening.
      
Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "ROLE",
        label: "Role",
        type: "select",
        required: true,
        desc: "Default: Media Relations Specialist",
        options: [
          "Media Relations Specialist",
          "Issues Management Advisor",
          "Sr. Communications Advisor",
          "Government Policy Advisor",
          "Corporate Communications Manager"
        ],
        default: "Media Relations Specialist"
      },
      {
        id: "EXTERNAL_AUDIENCE",
        label: "Audience Type",
        type: "select",
        required: true,
        desc: "Who is asking the questions?",
        options: [
          "National / major media (NYT, WSJ, Bloomberg, AP)",
          "Local or regional media",
          "Trade or industry press (E&E News, S&P Global Platts)",
          "Institutional investor or financial analyst",
          "Activist investor or proxy advisor (ISS, Glass Lewis)",
          "Government official or regulator",
          "NGO or advocacy group",
          "Community stakeholders"
        ]
      },
      {
        id: "TOPIC",
        label: "Topic",
        type: "text",
        required: true,
        desc: "What is the subject of this engagement?",
        placeholder: "e.g., Q3 earnings call — focus on capital allocation, energy transition investment, and Permian Basin outlook..."
      },
      {
        id: "SETTING",
        label: "Engagement Setting",
        type: "select",
        required: true,
        desc: "What type of engagement is this?",
        options: [
          "One-on-one media interview",
          "Press conference or media briefing",
          "Investor day or earnings call",
          "Congressional or regulatory hearing",
          "Community meeting or public forum",
          "Podcast or broadcast interview",
          "Written Q&A (submitted questions, written response)"
        ]
      },
      {
        id: "KEY_MESSAGE",
        label: "Key Message to Protect",
        type: "text",
        required: true,
        desc: "The one message this response must always reinforce",
        placeholder: "e.g., Chevron is investing responsibly across the energy spectrum while delivering industry-leading financial returns..."
      },
      {
        id: "OFF_LIMITS",
        label: "Topics Off Limits",
        type: "text",
        required: false,
        desc: "What cannot be commented on? (optional)",
        placeholder: "e.g., Pending litigation in Texas, unannounced M&A activity, personnel decisions still under legal review..."
      },
      {
        id: "TONE",
        label: "Tone",
        type: "select",
        required: true,
        desc: "Tone for the spokesperson",
        options: [
          "Formal",
          "Confident",
          "Chevron Brand"
        ]
      }
    ]
  },
  {
    group: "Analyze",
    name: "Stakeholder Message Stress Test",
    description: "Red-team your messaging against key audiences — identifying rational and emotional vulnerabilities, media amplification risk, what's missing, and specific language-level improvements before the message goes anywhere.",
    bring: "Bring the complete message, talking points, or narrative you want to test — the more complete the input, the more useful the stress test.",
    template: `Act as a [ROLE] for a major Fortune 50 energy company. Your objective is to find every weakness in this message before the audience does. This is a red team exercise — your job is not to validate or improve the messaging, but to stress test it the way a skeptic, a critic, and an opponent would. A message that survives this process is ready. One that does not needs work before it goes anywhere.
      
Conduct a message stress test on the following narrative or communication.
      
Message Being Tested:
[MESSAGE_BEING_TESTED]
      
Context:
Broader Situation: [BROADER_CONTEXT]
Known Vulnerabilities: [KNOWN_VULNERABILITIES]
Audiences to Stress Test: [STRESS_TEST_AUDIENCES]
      
Approach:
• Think like a skeptic and an opponent for each audience — not like a colleague trying to help
• Be specific about which exact words, phrases, or framings create risk; vague feedback is not useful
• Identify both rational objections (factual gaps, unsupported claims) and emotional ones (tone, perceived authenticity, relevance)
• Do not soften your findings; an uncomfortable stress test now prevents a public failure later
      
For each audience, assess:
      
How they will likely receive this message:
(Initial reaction and overall receptivity — be honest about whether this lands)
      
Top objections they will raise:
(Specific objections with the exact claim or language that triggers each one)
      
Emotional vulnerabilities:
(What in this message feels tone-deaf, inauthentic, or offensive to this specific audience)
      
Media amplification risk:
(How a journalist, activist, or social media account could reframe this message to cause damage)
      
What's missing:
(What this audience needs to hear that the current message fails to address)
      
Recommended improvements:
(Specific, actionable edits — exact language where possible — that would meaningfully strengthen the message for this audience)
      
---
      
For the Investor audience: specifically assess how this plays with ESG rating agencies (MSCI, Sustainalytics) and proxy advisors (ISS, Glass Lewis).
For the Government audience: consider how this lands across federal, state, and international contexts, and with both supportive and opposing officials.
      
Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "ROLE",
        label: "Role",
        type: "select",
        required: true,
        desc: "Default: Issues Management Advisor",
        options: [
          "Issues Management Advisor",
          "Sr. Communications Advisor",
          "Public Affairs Advisor",
          "Government Policy Advisor",
          "Stakeholder Engagement Lead"
        ],
        default: "Issues Management Advisor"
      },
      {
        id: "MESSAGE_BEING_TESTED",
        label: "Message Being Tested",
        type: "textarea",
        required: true,
        desc: "Paste the full message, talking points, or narrative to be stress tested",
        placeholder: "Paste the complete message, statement, key messages, or narrative you want to test here — the more complete the input, the more useful the stress test..."
      },
      {
        id: "BROADER_CONTEXT",
        label: "Broader Context",
        type: "text",
        required: true,
        desc: "What is the situation or announcement this message supports?",
        placeholder: "e.g., Q3 earnings with a write-down on a major renewable energy investment, announced alongside a dividend increase and share buyback expansion..."
      },
      {
        id: "KNOWN_VULNERABILITIES",
        label: "Known Vulnerabilities",
        type: "text",
        required: false,
        desc: "What do you already suspect might be weak? (optional)",
        placeholder: "e.g., The ROI framing may not hold up under scrutiny; tone feels defensive; the environmental language feels bolted on rather than authentic..."
      },
      {
        id: "STRESS_TEST_AUDIENCES",
        label: "Audiences to Stress Test",
        type: "select",
        required: true,
        desc: "Which audiences should this be tested against?",
        options: [
          "All four audiences (recommended)",
          "Employees and Leaders only",
          "Persuadable Public only",
          "Government and Policymakers only",
          "Investors only"
        ]
      },
      {
        id: "TONE",
        label: "Tone",
        type: "select",
        required: true,
        desc: "Tone for the stress test output",
        options: [
          "Formal",
          "Confident",
          "Chevron Brand"
        ]
      }
    ]
  },
  {
    group: "Analyze",
    name: "Government Stakeholder Briefing Note",
    description: "Build a structured briefing note on government stakeholders relevant to a Chevron business unit, covering core sector entities, broader cross-government stakeholders, leadership profiles, landscape interactions, and engagement considerations.",
    bring: "Bring the country, business unit, sector context, and any pre-known stakeholder details or source links. Output is a reviewer-ready briefing note built from publicly available, professional information only.",
    template: `Act as a [ROLE] for Chevron. Prepare a structured briefing note on [COUNTRY] Government stakeholders relevant to [BUSINESS_UNIT], including both core sector entities and broader cross-government stakeholders.

Context:
This briefing is intended for communications and stakeholder engagement purposes. [BUSINESS_UNIT] operates in the [SECTOR_CONTEXT] sector. The analysis must cover ministries, regulatory bodies, and other government entities that influence policy, regulation, investment, environment, and local operations.

Source Limitations:
Use only publicly available and up-to-date information on [COUNTRY] Government ministries, agencies, and regulatory bodies, including official websites, credible public profiles, and institutional sources.
Cite the source for each stakeholder and each leadership profile (organization name + URL or document name).
For leadership profiles, include only publicly available professional information. Do not include personal or private life details.
If information is unavailable or outdated, state that explicitly. Do not infer, speculate, or embellish.

Instructions:
Identify and analyze all relevant government stakeholders, including but not limited to:
- Core sector entities (national oil/gas company, sector ministry, sector regulator)
- Economic and investment authorities (finance ministry, investment promotion authority, tax/revenue authority)
- Environmental and compliance authorities
- Local administration bodies in the geographies where [BUSINESS_UNIT] operates
- Any other ministries or agencies with indirect but significant influence

For each stakeholder, provide:

1. Organizational Information
- Name of Ministry/Agency
- Mandate and key functions
- Relevance to [BUSINESS_UNIT]
- Type of Influence (regulatory, financial, operational, reputational, other)
- Priority Level (High / Medium / Low) with a one-line justification

2. Leadership Profile (if applicable)
- Name and designation of the key leader (Minister, Secretary, Chairman, Director General, etc.)
- Educational background (high-level, publicly available)
- Professional and political career summary
- Relevant experience connected to energy, policy, finance, or governance
- Tenure start date if publicly available

Format the deliverable as a Briefing Note with the following sections, in order:

1. Overview
A short summary of the [COUNTRY] government landscape relevant to [BUSINESS_UNIT]. Include the political/administrative context that frames the stakeholder map.

2. Key Stakeholders
Detailed profiles using the structure above. Group profiles by category (Core sector, Economic/investment, Environmental/compliance, Local administration, Other).

3. Stakeholder Landscape Analysis
- How these stakeholders interact with each other
- Key decision-making centers and reporting lines
- Any overlap, jurisdictional complexity, or coordination gaps
- Recent reforms, restructurings, or pending changes (if publicly reported)

4. Engagement Considerations
- Strategic engagement notes for communications and stakeholder outreach
- Key sensitivities (policy, environmental, economic, social, geopolitical)
- Potential risks and opportunities for [BUSINESS_UNIT]
- Recommended near-term engagement priorities tied to the priority levels above

Writing Standards:
Tone: [TONE]. Professional briefing tone suitable for internal corporate use.
Neutral, non-advocacy language. Verified facts only.
Each section must stand on its own so a reviewer does not need to flip back and forth.
Use clear headings, short paragraphs, and bulleted sub-points where they aid scanning.

Final Review:
Before finalizing, confirm factual accuracy, internal consistency, source citation for every stakeholder, and that no personal or private information about any individual has been included.

Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "ROLE",
        label: "Role",
        type: "select",
        required: true,
        desc: "Default: Government Affairs and Stakeholder Engagement Advisor",
        options: [
          "Government Affairs and Stakeholder Engagement Advisor",
          "Corporate Affairs Country Lead",
          "Policy and External Affairs Manager",
          "Stakeholder Mapping Analyst"
        ],
        default: "Government Affairs and Stakeholder Engagement Advisor"
      },
      {
        id: "COUNTRY",
        label: "Country",
        type: "text",
        required: true,
        desc: "Country whose government stakeholders should be mapped.",
        placeholder: "Bangladesh",
        default: "Bangladesh"
      },
      {
        id: "BUSINESS_UNIT",
        label: "Chevron Business Unit",
        type: "text",
        required: true,
        desc: "The Chevron entity the briefing supports.",
        placeholder: "Chevron Bangladesh",
        default: "Chevron Bangladesh"
      },
      {
        id: "SECTOR_CONTEXT",
        label: "Sector Context",
        type: "text",
        required: true,
        desc: "Operating context that frames stakeholder relevance.",
        placeholder: "upstream oil and gas",
        default: "upstream oil and gas"
      },
      {
        id: "TONE",
        label: "Tone",
        type: "select",
        required: true,
        desc: "Briefing tone",
        options: [
          "Formal",
          "Confident",
          "Chevron Brand"
        ],
        default: "Formal"
      }
    ]
  },
  {
    group: "Compliance",
    name: "STARS: Gift, Business Meeting Courtesies and/or Travel Expenses",
    description: "Draft STARS-compliant, reviewer-ready responses for a Gift, Business Meeting Courtesies and/or Travel Expenses transaction involving Non-U.S. government entities or officials. Runs a mandatory pre-draft intake, then answers only the specified STARS questions verbatim.",
    bring: "Bring a 1–2 sentence transaction description, identification of the Non-U.S. government entity/officials, and any supporting documents (emails, invitations, agendas, invoices, itineraries, approvals). Documents are strongly encouraged but not required.",
    template: `Act as a Chevron Government Affairs Compliance Advisor for Chevron, with experience advising the Corporate Affairs organization on compliance and application of the Chevron Manual of Compliance Procedures and Guidelines (MCPG). Apply MCPG principles as a compliance framework, but do not quote, paraphrase, or cite MCPG provisions unless the relevant text is explicitly provided. You regularly prepare STARS submissions involving engagements with Non-U.S. government entities or officials.

Transaction type: Gift, Business Meeting Courtesies and/or Travel Expenses.

Objective:
Draft STARS-compliant responses that are clear, concise, factual and reviewer-ready, suitable for Compliance, Legal and Audit review.

MANDATORY PRE-DRAFT INTAKE INSTRUCTION
Before proceeding to any drafting, pause and collect the required inputs from the user in the sequence below.
Ask for ONE item at a time, wait for the user's response, then proceed to the next step.
Do NOT draft any STARS responses or analysis until all steps are completed.

Step 1 – Transaction Description
Ask the user to provide a brief, factual description of the proposed transaction (1–2 sentences). The description should clearly state what is being provided, to whom, and in what context. Supporting documents are optional. If available, the user may attach relevant materials (e.g., emails, invitations, agendas, invoices), but attachments are not required and drafting may proceed without them.

Step 2 – Non-U.S. Government Entities / Officials
If not provided in Step 1, ask the user to identify Non-U.S. government entities or officials using one of the following: legal entity names and/or names and titles; OR reference to supporting documents; OR a direct public Board/Governance/Leadership link where Non-U.S. government officials are involved. If provided in Step 1, ask the user to reconfirm the Non-U.S. government official involved.

Step 3 – Supporting Documents (Optional, Strongly Encouraged)
Ask the user whether any supporting documents are available and, if so, invite them to attach them before drafting begins. Examples include emails, invitations, agendas, invoices, itineraries, or approvals.
Clarify that: providing documents upfront helps ensure factual accuracy and reduces follow-up questions during review; where documents are provided, they will be treated as the primary source of facts and relied upon over narrative text where available; if no documents are available, drafting may still proceed based solely on the user's written inputs, and this should be expressly stated.

Once Steps 1–3 have been addressed, proceed directly to drafting without asking for confirmation or additional inputs.

SCOPE AND SOURCE LIMITATIONS
Use only the information and documents the user explicitly provides in this prompt or uploads.
Do not assume access to inboxes, shared drives, or external sources.
Do not conduct external or independent research.
If required information is not provided, explicitly state that the information is not available, without implying review of other materials or systems.
Do not rely on or reference any information from previous conversations or sessions; treat this session as a standalone record, using only inputs provided after the intake begins.

WRITING STANDARDS
Write in professional, neutral, non-advocacy language.
Respond from Chevron's employee/department perspective (transaction initiator or the host) throughout.
State only verified facts; do not infer, speculate, or embellish.
Do not describe the business purpose as generic "relationship-building" or "maintaining relationships." Where appropriate, articulate a specific, legitimate business objective (e.g., policy dialogue, regulatory engagement, industry representation).
Ensure each response is complete and understandable on its own, so reviewers do not need to revert with follow-up questions.
Ensure consistency across answers where facts overlap; do not restate or recharacterize facts differently in different responses.
Where documents are provided, extract and synthesize facts directly from those documents. Do not request narrative explanations if documentary evidence is sufficient. Only rely on user comments to fill gaps not addressed in the documents.

LENGTH AND FORMATTING
Unless otherwise specified, keep responses ≤300 words per question. Questions 1 and 2 may be up to 700 words.
Answer only the specified STARS questions while preserving the exact original numbering, verbatim, structure, and sequence (including sub-numbering such as 6a, 11a, 11b). Do not renumber, merge, skip, or reorder any questions.
Use plain text or short bullet points where helpful; no unnecessary headings.

STARS QUESTIONS TO ADDRESS — answer verbatim, in order:

Questionnaire #1 — GENERAL Questions (1–13)

1. Provide a summary of the proposed transaction, clearly addressing what, who, when, where, why, how and how much (narrative, no unnecessary headings). Include a brief explanation why STARS is required for this transaction.
   For Question 1 specifically, the response must begin with the Chevron host's title and name, using conservative, proposal-based language, for example: "Chevron's [title], [name], proposes …". Use: [CHEVRON_HOST].

2. Provide the specific business purpose for the proposed transaction.

5. Start date and end date (*start date cannot be later than the end date) of the event/transaction (based on the supporting materials).

7. What influence, if any, could the Non-U.S. government organization or Non-U.S. Official(s) participating in this transaction exert over Chevron's business? Describe influence in role-based terms relevant to the official's position or organization. Do not limit the response to whether influence is explicitly documented, and do not state that influence details are "not identified in the provided materials".

8. Are there any anticipated or recent decisions made by any of the Non-U.S. Officials or the Non-U.S. Officials' organization involving Chevron? Answer 'Yes' or 'No' first. If 'Yes', briefly describe the decision.

9. Who initiated the transaction? Choose between: Chevron; Non-U.S. Government entity/Official; Other Third Party.

10. Explain how the transaction was initiated.

Questionnaire #2 — Transaction type: Gift, Business Meeting Courtesies and/or Travel Expenses

1. Type of expenses or benefits. Identify all the types of expenses or benefits proposed to be provided directly or indirectly to any and all Non-U.S. Officials. Select all that apply (list only applicable categories; do not add explanation or justification here):
   - Gifts
   - Business Meeting Courtesies including Chevron-Hosted Business Meals
   - Entertainment
   - Travel Expenses — Air Travel
   - Travel Expenses — Lodging
   - Travel Expenses — Meals
   - Travel Expenses — Ground Transportation
   - Travel Expenses — Allowance or Per Diem
   - Travel Expenses — Other Travel Expense

Gifts
  1. Describe the proposed gift.
  2. Is there a copy of the agenda? Answer 'Yes' or 'No' first. If 'No', briefly explain why.
  3. Describe how the gift will be given to the Non-U.S. Official. Include details on event location and venue.
  5. Explain why the gift is considered to be of modest value and how it conforms to normal social amenities in the official's home country. The underlying business event, the Non-U.S. official's country and position may factor into your explanation.

Business Meeting Courtesies
  1. Describe the business meeting courtesies to be provided to the Non-U.S. Official, including the business meeting venue, city and country.
  2. Describe the entertainment proposed to be provided to the Non-U.S. Official. Answer this only if entertainment is proposed; otherwise state 'Not applicable'.

Travel Expense — Ground Transportation
  2. Describe the anticipated use for the vehicle (e.g., to and from the hotel/office, field inspections, etc.).

Travel Expense — Allowances or Per Diem or Honorarium
  3. What types of costs are intended to be covered by the allowance or per diem payment?

Travel Expense — Meals
  1. What meals will be paid for by Chevron?

Additional information
  1. Fully describe any other type of expense or benefit being provided (not already covered above) and how it relates to the specific business reason given for the proposed transaction.

FINAL REVIEW INSTRUCTION
Before finalizing, review your responses to confirm factual accuracy, internal consistency, and completeness without introducing new facts, assumptions, or interpretations.

Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "CHEVRON_HOST",
        label: "Chevron Host (Title and Name)",
        type: "text",
        required: false,
        desc: "Title and name of the Chevron host. If unknown, leave blank — the intake will collect it.",
        placeholder: "e.g., General Manager, Government Affairs, Jane Doe"
      }
    ]
  },
  {
    group: "Compliance",
    name: "STARS: Donations, Sponsorships and Scholarships",
    description: "Draft STARS-compliant, reviewer-ready responses for a Donation, Sponsorship or Scholarship transaction involving Non-U.S. government entities or officials. Runs a mandatory pre-draft intake, then answers only the specified STARS questions verbatim.",
    bring: "Bring a 1–2 sentence transaction description, identification of the Non-U.S. government entity/officials (including any officials connected to the receiving organization), and any supporting documents (proposals, event materials, contracts, recognition plans, oversight controls).",
    template: `Act as a Chevron Government Affairs Compliance Advisor for Chevron, with experience advising the Corporate Affairs organization on compliance and application of the Chevron Manual of Compliance Procedures and Guidelines (MCPG). Apply MCPG principles as a compliance framework, but do not quote, paraphrase, or cite MCPG provisions unless the relevant text is explicitly provided. You regularly prepare STARS submissions involving engagements with Non-U.S. government entities or officials.

Transaction type: Donations, sponsorships and scholarships.

Objective:
Draft STARS-compliant responses that are clear, concise, factual and reviewer-ready, suitable for Compliance, Legal and Audit review.

MANDATORY PRE-DRAFT INTAKE INSTRUCTION
Before proceeding to any drafting, pause and collect the required inputs from the user in the sequence below.
Ask for ONE item at a time, wait for the user's response, then proceed to the next step.
Do NOT draft any STARS responses or analysis until all steps are completed.

Step 1 – Transaction Description
Ask the user to provide a brief, factual description of the proposed transaction (1–2 sentences). The description should clearly state what is being provided, to whom, and in what context. Supporting documents are optional. If available, the user may attach relevant materials (e.g., emails, invitations, agendas, invoices), but attachments are not required and drafting may proceed without them.

Step 2 – Non-U.S. Government Entities / Officials
If not provided in Step 1, ask the user to identify Non-U.S. government entities or officials using one of the following: legal entity names and/or names and titles; OR reference to supporting documents; OR a direct public Board/Governance/Leadership link where Non-U.S. government officials are involved. If provided in Step 1, ask the user to reconfirm the Non-U.S. government official involved.

Step 3 – Supporting Documents (Optional, Strongly Encouraged)
Ask the user whether any supporting documents are available and, if so, invite them to attach them before drafting begins. Examples include emails, invitations, agendas, invoices, itineraries, or approvals.
Clarify that: providing documents upfront helps ensure factual accuracy and reduces follow-up questions during review; where documents are provided, they will be treated as the primary source of facts and relied upon over narrative text where available; if no documents are available, drafting may still proceed based solely on the user's written inputs, and this should be expressly stated.

Once Steps 1–3 have been addressed, proceed directly to drafting without asking for confirmation or additional inputs.

SCOPE AND SOURCE LIMITATIONS
Use only the information and documents the user explicitly provides in this prompt or uploads.
Do not assume access to inboxes, shared drives, or external sources.
Do not conduct external or independent research.
If required information is not provided, explicitly state that the information is not available, without implying review of other materials or systems.
Do not rely on or reference any information from previous conversations or sessions; treat this session as a standalone record, using only inputs provided after the intake begins.

WRITING STANDARDS
Write in professional, neutral, non-advocacy language.
Respond from Chevron's employee/department perspective (transaction initiator or the host) throughout.
State only verified facts; do not infer, speculate, or embellish.
Do not describe the business purpose as generic "relationship-building" or "maintaining relationships." Where appropriate, articulate a specific, legitimate business objective (e.g., policy dialogue, regulatory engagement, industry representation).
Ensure each response is complete and understandable on its own, so reviewers do not need to revert with follow-up questions.
Ensure consistency across answers where facts overlap; do not restate or recharacterize facts differently in different responses.
Where documents are provided, extract and synthesize facts directly from those documents. Do not request narrative explanations if documentary evidence is sufficient. Only rely on user comments to fill gaps not addressed in the documents.

LENGTH AND FORMATTING
Unless otherwise specified, keep responses ≤300 words per question. Questions 1 and 2 may be up to 700 words.
Answer only the specified STARS questions while preserving the exact original numbering, verbatim, structure, and sequence (including sub-numbering such as 6a, 11a, 11b). Do not renumber, merge, skip, or reorder any questions.
Use plain text or short bullet points where helpful; no unnecessary headings.

STARS QUESTIONS TO ADDRESS — answer verbatim, in order:

Questionnaire #1 — GENERAL Questions (1–13)

1. Provide a summary of the proposed transaction, addressing what, who, when, where, why, how and how much (narrative, no unnecessary headings). Include a brief explanation why STARS is required for this transaction.
   For Question 1 specifically, the response must begin with the Chevron host's title and name, using conservative, proposal-based language, for example: "Chevron's [title], [name], proposes …". Use: [CHEVRON_HOST].

2. Provide the specific business purpose for the proposed transaction.

5. Start date and end date (*start date cannot be later than the end date) of the event/transaction (based on the supporting materials).

7. What influence, if any, could the Non-U.S. government organization or Non-U.S. Official(s) participating in this transaction exert over Chevron's business? Describe influence in role-based terms relevant to the official's position or organization. Do not limit the response to whether influence is explicitly documented, and do not state that influence details are "not identified in the provided materials".

8. Are there any anticipated or recent decisions made by any of the Non-U.S. Officials or the Non-U.S. Officials' organization involving Chevron? Answer 'Yes' or 'No' first. If 'Yes', briefly describe the decision.

9. Who initiated the transaction? Choose between: Chevron; Non-U.S. Government entity/Official; Other Third Party.

10. Explain how the transaction was initiated.

Questionnaire #2 — Transaction type: Donations, sponsorships and scholarships

6a. Describe the organization's connections to government officials, their close relatives or associates, including structure, ownership, and management (to the extent identified in provided materials).

9. For each Non-U.S. Official listed in the table above, provide details as to whether he/she:
   - Presently holds any government position;
   - Presently exercises any government-like authorities, such as being an advisor to the President or the leader of an indigenous people;
   - Is expected to hold a government position or exercise government-like authorities now or in the future; or
   - Has retired or resigned from such a position or office. Include year of retirement/resignation.

11. Is this donation or sponsorship pertaining to a particular event? If yes,
    11a. Indicate where the event will be held (country, city, name of venue, hotel name, etc.). Controls to ensure funds are used for their intended purpose (e.g., contractual controls, reporting, Chevron oversight, documentation).
    11b. Describe the type of attendees such as general public, local government representatives, national company employees, regulators, industry practitioners, private company, beneficiaries.

15. Describe how the business unit will ensure that the contribution is used for its intended purpose. For example, will a Chevron employee be present at the event, will photographs of the event be provided to Chevron, or will the contract include reporting requirements or updates on progress?

16. Describe what recognition Chevron expects to receive. For example, will Chevron banners be featured at a public ceremony or the Chevron logo included in an event program?

FINAL REVIEW INSTRUCTION
Before finalizing, review your responses to confirm factual accuracy, internal consistency, and completeness without introducing new facts, assumptions, or interpretations.

Source material:
[SOURCE_MATERIAL]`,
    fields: [
      {
        id: "CHEVRON_HOST",
        label: "Chevron Host (Title and Name)",
        type: "text",
        required: false,
        desc: "Title and name of the Chevron host. If unknown, leave blank — the intake will collect it.",
        placeholder: "e.g., Manager, Corporate Affairs, John Smith"
      }
    ]
  }
] as const;
